import { useCallback, useEffect, useState } from 'react'
import {
  adminTestPush,
  listRegisteredDevices,
  type AdminTestPushResponse,
  type PushMode,
  type PushPriority,
  type RegisteredDevice,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { usePermission } from '../../hooks/usePermission'

const MODE_OPTIONS: { value: PushMode; label: string; help: string }[] = [
  { value: 'auto', label: 'Auto', help: 'notification block when title/body present, else data-only' },
  { value: 'notification', label: 'Notification', help: 'Forces a notification block — system displays alert' },
  { value: 'data_only', label: 'Data only', help: 'No notification block — app must handle delivery (silent on iOS unless content-available)' },
  { value: 'mixed', label: 'Mixed', help: 'Both notification AND data — system shows alert and app gets data' },
]

const PRIORITY_OPTIONS: { value: PushPriority; label: string; help: string }[] = [
  { value: 'high', label: 'High', help: 'Required for killed-state delivery on Android' },
  { value: 'normal', label: 'Normal', help: 'Power-saver — may be delayed' },
]

export function Notifications() {
  const canTestPush = usePermission('notifications', 'test_push')

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 300)
  const [devices, setDevices] = useState<RegisteredDevice[]>([])
  const [loadingDevices, setLoadingDevices] = useState(false)
  const [deviceError, setDeviceError] = useState<string | null>(null)

  const [selected, setSelected] = useState<RegisteredDevice | null>(null)

  const [title, setTitle] = useState('Test Push')
  const [body, setBody] = useState('Hello from the admin portal.')
  const [deeplink, setDeeplink] = useState('')
  const [category, setCategory] = useState('')
  const [dataJson, setDataJson] = useState('{"foo":"bar"}')
  const [mode, setMode] = useState<PushMode>('auto')
  const [priority, setPriority] = useState<PushPriority>('high')
  const [contentAvailable, setContentAvailable] = useState(false)

  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [lastResponse, setLastResponse] = useState<AdminTestPushResponse | null>(null)

  const loadDevices = useCallback(async () => {
    setLoadingDevices(true)
    setDeviceError(null)
    try {
      const response = await listRegisteredDevices({ search: debouncedSearch || undefined, limit: 100 })
      if (response.ok && response.data) {
        setDevices(response.data.items)
      } else {
        setDeviceError(response.error?.message || 'Failed to load registered devices')
        setDevices([])
      }
    } catch (e) {
      setDeviceError(e instanceof Error ? e.message : 'Failed to load registered devices')
    } finally {
      setLoadingDevices(false)
    }
  }, [debouncedSearch])

  useEffect(() => {
    loadDevices()
  }, [loadDevices])

  async function handleSend(target: 'device' | 'user') {
    if (!selected) return
    setSending(true)
    setSendError(null)
    setLastResponse(null)
    let parsedData: Record<string, string> | undefined
    if (dataJson.trim()) {
      try {
        const parsed = JSON.parse(dataJson)
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          parsedData = Object.fromEntries(
            Object.entries(parsed).map(([k, v]) => [k, String(v)]),
          )
        }
      } catch {
        setSendError('Custom data is not valid JSON')
        setSending(false)
        return
      }
    }
    try {
      const response = await adminTestPush({
        device_id: target === 'device' ? selected.device_id : undefined,
        user_id: target === 'user' ? selected.user_id : undefined,
        title: title || undefined,
        body: body || undefined,
        data: parsedData,
        deeplink: deeplink || undefined,
        category: category || undefined,
        mode,
        priority,
        content_available: contentAvailable,
      })
      if (response.ok && response.data) {
        setLastResponse(response.data)
      } else {
        setSendError(response.error?.message || 'Send failed')
      }
    } catch (e) {
      setSendError(e instanceof Error ? e.message : 'Send failed')
    } finally {
      setSending(false)
    }
  }

  if (!canTestPush) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-semibold mb-2">Notifications</h1>
        <p className="text-gray-600 dark:text-gray-400">
          You don't have permission to trigger test pushes. Ask a super admin to grant you the
          <code className="mx-1 px-1 rounded bg-gray-200 dark:bg-gray-800">notifications:test_push</code>
          permission.
        </p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Push Notification Testing</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Pick a registered device and fire a test push. Use the controls below to test
          how the mobile client handles each FCM payload shape across foreground,
          background, and killed app states.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Device list */}
        <section className="lg:col-span-1 space-y-3 bg-white dark:bg-[#1a1620] border border-gray-200 dark:border-[#3d2c4f] rounded-lg p-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Registered devices</h2>
            <CustomButton size="sm" variant="ghost" onClick={loadDevices} loading={loadingDevices}>
              Refresh
            </CustomButton>
          </div>
          <CustomTextInput
            placeholder="Search by user email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {deviceError && (
            <p className="text-sm text-red-600 dark:text-red-400">{deviceError}</p>
          )}
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {devices.length === 0 && !loadingDevices && (
              <p className="text-sm text-gray-500">No devices match.</p>
            )}
            {devices.map((d) => (
              <button
                key={d.id}
                onClick={() => setSelected(d)}
                className={`w-full text-left p-3 rounded-md border transition-colors ${
                  selected?.id === d.id
                    ? 'border-[#7f4f9a] bg-[#f5edff] dark:bg-[#2a1f3d]'
                    : 'border-gray-200 dark:border-[#3d2c4f] hover:bg-gray-50 dark:hover:bg-[#241b2e]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate">
                    {d.user_email || d.user_id.slice(0, 8)}
                  </span>
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded ${
                      d.platform === 'ios'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                        : d.platform === 'android'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {d.platform}
                  </span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono truncate">
                  device {d.device_id.slice(0, 8)} · token {d.token_preview}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  status: <span className={d.status === 'active' ? 'text-emerald-600' : 'text-red-500'}>{d.status}</span>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Composer */}
        <section className="lg:col-span-2 space-y-4 bg-white dark:bg-[#1a1620] border border-gray-200 dark:border-[#3d2c4f] rounded-lg p-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Test payload</h2>
            {selected && (
              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                Targeting: {selected.user_email || selected.user_id.slice(0, 8)} · {selected.device_id.slice(0, 8)}
              </span>
            )}
          </div>

          {!selected && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Select a device on the left to enable sending.
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CustomTextInput
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Notification title"
            />
            <CustomTextInput
              label="Category / channel"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="orders, social, from_barta…"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#0f0d12] px-3 py-2 text-sm"
            />
          </div>
          <CustomTextInput
            label="Deeplink"
            value={deeplink}
            onChange={(e) => setDeeplink(e.target.value)}
            placeholder="barta://orders/123"
          />
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Custom data (JSON object — values are coerced to strings per FCM spec)
            </label>
            <textarea
              value={dataJson}
              onChange={(e) => setDataJson(e.target.value)}
              rows={3}
              className="mt-1 w-full font-mono text-xs rounded-md border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#0f0d12] px-3 py-2"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">FCM Mode</label>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value as PushMode)}
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#0f0d12] px-3 py-2 text-sm"
              >
                {MODE_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {MODE_OPTIONS.find((m) => m.value === mode)?.help}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as PushPriority)}
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#0f0d12] px-3 py-2 text-sm"
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {PRIORITY_OPTIONS.find((p) => p.value === priority)?.help}
              </p>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={contentAvailable}
              onChange={(e) => setContentAvailable(e.target.checked)}
            />
            <span>iOS <code>content-available</code> (silent background data delivery)</span>
          </label>

          <div className="flex flex-wrap gap-2">
            <CustomButton
              variant="primary"
              disabled={!selected || sending}
              loading={sending}
              onClick={() => handleSend('device')}
            >
              Send to this device
            </CustomButton>
            <CustomButton
              variant="secondary"
              disabled={!selected || sending}
              loading={sending}
              onClick={() => handleSend('user')}
            >
              Send to all devices for user
            </CustomButton>
          </div>

          {sendError && (
            <p className="text-sm text-red-600 dark:text-red-400">{sendError}</p>
          )}

          {lastResponse && (
            <div className="mt-4 p-3 rounded-md border border-gray-200 dark:border-[#3d2c4f] bg-gray-50 dark:bg-[#15111c]">
              <p className="text-sm font-medium mb-2">
                Sent {lastResponse.sent} / {lastResponse.results.length}
              </p>
              <ul className="space-y-1">
                {lastResponse.results.map((r, i) => (
                  <li
                    key={i}
                    className={`text-xs font-mono ${
                      r.success ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {r.success ? 'OK' : 'FAIL'} · {r.target}
                    {r.fcm_message_id && ` · ${r.fcm_message_id}`}
                    {r.error && ` · ${r.error}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
